import { Component, OnInit, NgZone, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, take } from 'rxjs/operators';
import { InviteComponent } from '../invite/invite.component';
import { HistoryCandidatesComponent } from '../history/history.component';
import { UtilsService } from 'src/app/services/utils.service';
import { CandidateService } from 'src/app/services/candidate.service';
import { resetCandidates, setCandidates } from 'src/app/actions/candidates.actions';
import { setCandidateSearch } from 'src/app/actions/candidate-search.actions';
import { setSelectedCandidates } from 'src/app/actions/selected-candidates.actions';
import { fromCandidates } from 'src/app/reducers/candidates.reducer';
import { fromCandidateSearch } from 'src/app/reducers/candidate-search.reducer';
import { Pagination } from 'src/app/models/pagination.model';
import { Candidate } from 'src/app/models/candidate.model';
import { Router } from '@angular/router';
import { fromSelectedCandidates } from 'src/app/reducers/selected-candidates.reducer';
import { CustomValidators } from '../../../helpers/custom-validators';
import { Consultant } from 'src/app/models/consultant.model';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { AppConfig } from 'src/app/helpers/app.config';

@Component({
  selector: 'ct-candidates',
  templateUrl: './candidates.component.html',
  styleUrls: ['./candidates.component.scss']
})
export class CandidatesComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(InviteComponent) invite;

  candidates$: Observable<Pagination<Candidate>>;
  consultant$: Observable<Consultant>;

  consultantSubscription: Subscription;

  candidateSearchForm: FormGroup = this.fb.group({
    candidateId: ['', [Validators.minLength(3), CustomValidators.validCandidateIdCharacters]],
    email: ['', Validators.email],
    idCard: ['', [CustomValidators.validCharacters]],
    name: ['', [Validators.minLength(3), CustomValidators.requiredWith('surname'), CustomValidators.validCharacters]],
    surname: ['', [Validators.minLength(3), CustomValidators.requiredWith('name'), CustomValidators.validCharacters]]
  });

  constructor(
    private store: Store,
    private ngZone: NgZone,
    private fb: FormBuilder,
    private utilsService: UtilsService,
    private candidateService: CandidateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.store.dispatch(resetCandidates());

    this.candidates$ = this.store.pipe(select(fromCandidates.selectCandidatesState));
    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));

    // this.store.pipe(select(fromCandidateSearch.selectCandidateSearchState)).subscribe(candidateSearch => {
    //   if (candidateSearch) {
    //     this.candidateSearchForm.setValue({
    //       candidateId: candidateSearch.candidateId,
    //       email: candidateSearch.email,
    //       idCard: candidateSearch.idCard,
    //       name: candidateSearch.name,
    //       surname: candidateSearch.surname
    //     });
    //   }
    // });

    this.candidateSearchForm.get('name').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.candidateSearchForm.get('surname').updateValueAndValidity();
      this.candidateSearchForm.get('surname').markAsTouched();
    });
    this.candidateSearchForm.get('surname').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.candidateSearchForm.get('name').updateValueAndValidity();
      this.candidateSearchForm.get('name').markAsTouched();
    });

    this.consultant$.subscribe(consultant => {

      // añadimos la validación del campo de documento seún el país
      const idCardInput = this.candidateSearchForm.controls.idCard;

      if (consultant.client.id == 1) {
        idCardInput.setValidators([CustomValidators.documentOrPassportValidator, CustomValidators.validCharacters]);
      } else {
        idCardInput.setValidators([CustomValidators.validCharacters]);
      }
    })
  }

  ngAfterViewInit(): void {
    // Si ya esta empezada la invitación (venimos de crear un procesos) lanzamos la modal
    if (localStorage.getItem('lastUrl')?.includes('/processes/create') && localStorage.getItem('inviteInitied') === 'true') {
      this.invite.open();
    }
    localStorage.setItem('lastUrl', this.router.url);
  }

  onSubmit() {
    if (!Object.values(this.candidateSearchForm.value).every(value => value === '')) {
      this.store.dispatch(setCandidateSearch(this.candidateSearchForm.value));
      this.searchCandidates(1);
    } else {
      this.utilsService.toast('warning', 'AtLeastOneSearchParameter');
    }
  }

  searchCandidatesFromHistoric() {
    let currentPage;
    this.candidates$.subscribe(candidate => {
      currentPage = candidate.currentPage;
    });
    this.searchCandidates(currentPage);
  }

  searchCandidates(page: number) {
    this.consultantSubscription = this.consultant$.pipe(distinctUntilKeyChanged('office')).subscribe(consultant => {
      this.ngZone.run(() => {
        this.candidateService.search(page, consultant.office.id, consultant.client.id).pipe(take(1)).subscribe(
          candidates => this.store.dispatch(setCandidates(candidates)),
          error => this.utilsService.toast('error', 'CannotGetCandidates', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /candidates?origin=ct', body: { page: page, consultantOfficeId: consultant.office.id }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }))
        );
      });
    });
  }

  openInvite(candidate: Candidate) {
    this.store.dispatch(setSelectedCandidates({ selectedCandidates: [candidate] }));
    this.invite.open();
  }

  openHistory(candidate: Candidate) {
    this.router.navigate(['consultant/candidates/history/' + candidate.id]);
  }

  ngOnDestroy(): void {
    if (this.consultantSubscription) {
      this.consultantSubscription.unsubscribe();
    }
  }

}
